import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { isDevelopment } from '../constants';

const DWELL_MS = 90_000;

const TAG_REGISTRATION_STEP = 'replay.registration_step';
const TAG_PERSONAL_DATA = 'replay.personal_data';
const TAG_TRIGGER = 'replay.trigger';
const TRIGGER_VALUE = 'dwell_90s';

interface UseSentryReplayOnDwellOptions {
  enabled?: boolean;
  stepTag: string;
}

const clearReplayTags = (): void => {
  Sentry.setTag(TAG_REGISTRATION_STEP, undefined);
  Sentry.setTag(TAG_PERSONAL_DATA, undefined);
  Sentry.setTag(TAG_TRIGGER, undefined);
};

export const useSentryReplayOnDwell = ({ enabled = true, stepTag }: UseSentryReplayOnDwellOptions): void => {
  const isRecordingRef = useRef(false);

  useEffect(() => {
    if (!enabled || isDevelopment || !process.env.REACT_APP_SENTRY_DSN) {
      return;
    }
    const timerId = window.setTimeout(() => {
      const replay = Sentry.getReplay();
      if (!replay) {
        return;
      }

      Sentry.setTag(TAG_REGISTRATION_STEP, stepTag);
      Sentry.setTag(TAG_PERSONAL_DATA, 'true');
      Sentry.setTag(TAG_TRIGGER, TRIGGER_VALUE);

      if (replay.getReplayId()) {
        void replay.flush();
      } else {
        replay.start();
      }

      isRecordingRef.current = true;
    }, DWELL_MS);

    return () => {
      window.clearTimeout(timerId);

      if (!isRecordingRef.current) {
        return;
      }

      const replay = Sentry.getReplay();

      if (replay) {
        void replay.stop();
      }

      clearReplayTags();
      isRecordingRef.current = false;
    };
  }, [enabled, stepTag]);
};
